
import { getPack } from '../packs/packStore';
import { type ContentPack, type SearchIndexPack } from '../packs/manifest';

let contentPack: ContentPack | null = null;
let searchPack: SearchIndexPack | null = null;

// Initialize: load packs from IDB into memory
// Returns true if loaded, false if missing (needs install)
export async function loadBibleData(): Promise<boolean> {
    if (contentPack && searchPack) return true;

    try {
        const c = await getPack('douay_rheims_v1') as ContentPack;
        const s = await getPack('douay_rheims_v1_search') as SearchIndexPack;

        if (c && s) {
            contentPack = c;
            searchPack = s;
            return true;
        }
    } catch (e) {
        console.error('Failed to load packs from IDB', e);
    }
    return false;
}

// Book ID slugification (safe, deterministic)
const toBookId = (displayName: string): string => {
    return displayName.toLowerCase()
        .replace('&', 'and')
        .replace(/^i\s/, '1-').replace(/^ii\s/, '2-').replace(/^iii\s/, '3-')
        .replace(/\s+/g, '-')
        .replace(/[^a-z0-9-]/g, ''); // Collapse or strip
};

let bookNameById: Map<string, string> = new Map();
let bookIdByName: Map<string, string> = new Map();

export function getTableOfContents() {
    if (!contentPack) return [];

    const toc = Object.entries(contentPack).map(([id, book]) => ({
        id,
        name: book.name,
        chapterCount: Object.keys(book.chapters).length
    }));

    // Initialize maps on first access if empty
    if (bookNameById.size === 0) {
        toc.forEach(b => {
            bookNameById.set(b.id, b.name);
            bookIdByName.set(toBookId(b.name), b.id);
            // Also add the exact lowercased name just in case the AI outputs "genesis" derived from ID
            bookIdByName.set(b.name.toLowerCase(), b.id);
        });
    }

    return toc;
}

export function getBookIdFromSlug(slug: string): string | undefined {
    // Exact match?
    if (bookIdByName.has(slug)) return bookIdByName.get(slug);
    // Maybe slugified?
    if (bookIdByName.has(toBookId(slug))) return bookIdByName.get(toBookId(slug));

    // Fallback: try to find loose match
    // E.g. ai sends "song-of-sol" -> "song-of-solomon" (hard without fuzzy search, but basic slug match is robust enough for now)
    return undefined;
}

export function getBookName(id: string): string {
    return bookNameById.get(id) || id;
}

export function getChapter(bookId: string, chapter: number) {
    if (!contentPack) return null;
    const book = contentPack[bookId];
    if (!book) return null;
    return book.chapters[chapter];
}

export function searchBible(query: string) {
    if (!searchPack || !contentPack) return [];

    const tokens = query.toLowerCase().replace(/[^a-z0-9\s]/g, '').split(/\s+/).filter(t => t.length > 2);
    if (tokens.length === 0) return [];

    // Intersection of results for all tokens
    let resultIds: Set<string> | null = null;

    for (const token of tokens) {
        const ids = searchPack[token] || [];
        if (!resultIds) {
            resultIds = new Set(ids);
        } else {
            // Intersect
            const nextSet = new Set<string>();
            for (const id of ids) {
                if (resultIds.has(id)) nextSet.add(id);
            }
            resultIds = nextSet;
            if (resultIds.size === 0) break;
        }
    }

    if (!resultIds || resultIds.size === 0) return [];

    // Hydrate results
    return Array.from(resultIds).slice(0, 50).map(vid => {
        // vid format: dr1899:bookId:chapter:verse
        const parts = vid.split(':');
        const bId = parts[1];
        const ch = parseInt(parts[2]);
        const v = parseInt(parts[3]);
        const text = contentPack?.[bId]?.chapters?.[ch]?.[v] || '';
        const bookName = contentPack?.[bId]?.name || bId;

        return {
            vid,
            bookId: bId,
            bookName,
            chapter: ch,
            verse: v,
            text
        };
    });
}
